import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageTitle from "./include/PageTitle";
import Loader from "../Components/Loader";
import { pickLocalizedValue, useI18n } from "../i18n/I18nContext";
import { API_BASE, API_BASES, fetchApiJson } from "../config/apiBase";
import { formatPropertyPrice, getPropertyPriceSuffix } from "../Utils/propertyPricing";
import { useTranslatedCopy } from "../i18n/useTranslatedCopy";
import Seo, { mergeSeoKeywords } from "../Components/Seo";

const fallbackPropertyImage = "/img/property-placeholder.svg";

const propertyShowcaseMainStyle = {
  gridColumn: 2,
  width: "100%",
  aspectRatio: "16 / 9",
  minHeight: 360,
  maxHeight: 680,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const propertyShowcaseSideStyle = {
  width: "100%",
  aspectRatio: "4 / 3",
  minHeight: 180,
};

const propertyShowcaseMainImageStyle = {
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "center center",
};

const propertyShowcaseSideImageStyle = {
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center center",
};

const resolvePropertyMediaUrl = (url) => {
  if (!url) return fallbackPropertyImage;
  if (/^data:image\//i.test(url)) return url;
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/(uploads|api)\//i.test(url)) {
    return `${API_BASE}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/") || url.startsWith("/api/")) return `${API_BASE}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
};

const normalizeAmenities = (amenities) => {
  if (!amenities) return [];
  if (Array.isArray(amenities)) return amenities.filter(Boolean).map(String);
  if (typeof amenities === "string") {
    const raw = amenities.trim();
    // When saved from admin we store JSON string like: ["Pool","Gym"]
    if ((raw.startsWith("[") && raw.endsWith("]")) || (raw.startsWith("{") && raw.endsWith("}"))) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
        if (parsed && typeof parsed === "object" && Array.isArray(parsed.items)) {
          return parsed.items.filter(Boolean).map(String);
        }
      } catch {
        // fall back to comma split below
      }
    }
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  if (typeof amenities === "object" && Array.isArray(amenities.items)) {
    return amenities.items.filter(Boolean).map(String);
  }
  return [];
};

const chunkIntoColumns = (items, columns) => {
  const cols = Array.from({ length: columns }, () => []);
  items.forEach((item, idx) => cols[idx % columns].push(item));
  return cols;
};

const isSocialVideoUrl = (url) => /instagram\.com|tiktok\.com/i.test(String(url || ""));

const normalizeVideoUrl = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^(www\.)?(instagram\.com|tiktok\.com)/i.test(raw)) return `https://${raw.replace(/^www\./i, "www.")}`;
  return raw;
};

const getPropertyStatusLabel = (property, ui) => {
  const status = String(property?.status || "").toUpperCase();
  if (status === "SOLD") return ui.sold;
  if (status === "RENTED") return ui.rented;
  return property?.listingType === "RENT" ? ui.forRent : ui.forSale;
};

const hasDisplayValue = (value) => {
  if (value == null) return false;
  if (typeof value === "number") return !Number.isNaN(value);
  const text = String(value).trim();
  if (!text) return false;
  if (text === "—" || text === "-" || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
    return false;
  }
  return true;
};

const renderFormattedText = (value, fallback) => {
  const text = String(value || fallback || "").replace(/\r\n?/g, "\n").trim();
  const paragraphs = text ? text.split(/\n{2,}/) : [fallback];

  return paragraphs.map((paragraph, paragraphIndex) => (
    <p key={`property-description-${paragraphIndex}`}>
      {String(paragraph)
        .split("\n")
        .map((line, lineIndex) => (
          <React.Fragment key={`property-description-${paragraphIndex}-${lineIndex}`}>
            {lineIndex > 0 ? <br /> : null}
            {line}
          </React.Fragment>
        ))}
    </p>
  ));
};

export default function PropertyDetail() {
  const { id } = useParams();
  const { locale, t } = useI18n();
  const [property, setProperty] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inquiryForm, setInquiryForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [inquiryFeedback, setInquiryFeedback] = useState({ type: "", message: "" });
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const trackedPropertyEventKey = useRef("");
  const localizedProperty = property;
  const localizedRelated = related;

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        setProperty(null);

        const { json } = await fetchApiJson(`/api/properties/${id}?lang=${locale}`);
        if (!json?.success) throw new Error(json?.message || "Failed to load property");
        if (!ignore) setProperty(json.data);
      } catch (e) {
        if (!ignore) {
          setError(
            e?.message ||
            `Could not load property from any configured API base: ${API_BASES.join(", ")}`
          );
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    if (id) load();
    return () => {
      ignore = true;
    };
  }, [id, locale]);

  useEffect(() => {
    if (typeof window === "undefined" || !property?.id) return;

    const eventKey = `${property.id}:${locale}`;
    if (trackedPropertyEventKey.current === eventKey) return;
    trackedPropertyEventKey.current = eventKey;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "property_view",
      page_type: "property_detail",
      content_name: pickLocalizedValue(property, "title", locale, "Property Detail"),
      content_category: "Real Estate",
      content_ids: [property.id],
      content_type: "product",
      property_id: property.id,
      property_status: property.status || "",
      listing_type: property.listingType || "",
      value: property.price ?? undefined,
      currency: "SAR",
      locale,
    });
  }, [locale, property]);

  useEffect(() => {
    let ignore = false;
    const loadRelated = async () => {
      try {
        const { json } = await fetchApiJson(`/api/properties?lang=${locale}`);
        if (!json?.success) return;
        const items = Array.isArray(json.data) ? json.data : [];
        const filtered = items.filter((p) => p?.id && p.id !== id).slice(0, 2);
        if (!ignore) setRelated(filtered);
      } catch {
        // ignore
      }
    };

    if (property?.id) loadRelated();
    return () => {
      ignore = true;
    };
  }, [property?.id, id, locale]);

  const images = useMemo(() => {
    const list = Array.isArray(localizedProperty?.images) ? localizedProperty.images : [];
    const sortedList = [...list].sort((a, b) => {
      if (Boolean(a?.isPrimary) !== Boolean(b?.isPrimary)) return a?.isPrimary ? -1 : 1;
      return (Number(a?.sortOrder) || 0) - (Number(b?.sortOrder) || 0);
    });
    const urls = sortedList
      .map((img) => resolvePropertyMediaUrl(img?.imageUrl))
      .filter(Boolean);
    const fallback = resolvePropertyMediaUrl(localizedProperty?.coverImage || localizedProperty?.image || null);
    const all = [...urls];
    if (fallback && !all.includes(fallback)) all.unshift(fallback);
    return all.length ? all : [fallbackPropertyImage];
  }, [localizedProperty]);

  const galleryImages = useMemo(() => images.slice(0, 5), [images]);
  const primaryImage = images[0] || fallbackPropertyImage;
  const safeActiveImageIndex = images[activeImageIndex] ? activeImageIndex : 0;
  const showcaseImage = images[safeActiveImageIndex] || fallbackPropertyImage;
  const hasMultipleImages = images.length > 1;
  const previousImageIndex = hasMultipleImages ? (safeActiveImageIndex <= 0 ? images.length - 1 : safeActiveImageIndex - 1) : 0;
  const nextImageIndex = hasMultipleImages ? (safeActiveImageIndex >= images.length - 1 ? 0 : safeActiveImageIndex + 1) : 0;
  const isLightboxOpen = lightboxIndex >= 0 && lightboxIndex < images.length;

  useEffect(() => {
    setActiveImageIndex(0);
  }, [id, images.length, primaryImage]);

  useEffect(() => {
    if (!isLightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setLightboxIndex(-1);
      } else if (event.key === "ArrowLeft") {
        setLightboxIndex((current) => (current <= 0 ? images.length - 1 : current - 1));
      } else if (event.key === "ArrowRight") {
        setLightboxIndex((current) => (current >= images.length - 1 ? 0 : current + 1));
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [images.length, isLightboxOpen]);

  const amenities = useMemo(() => normalizeAmenities(localizedProperty?.amenities), [localizedProperty?.amenities]);
  const amenityColumns = useMemo(() => chunkIntoColumns(amenities, 3), [amenities]);

  const mapEmbedUrl = localizedProperty?.mapEmbedUrl || "";
  const hasMap = Boolean(mapEmbedUrl);

  const videoUrl = normalizeVideoUrl(localizedProperty?.videoUrl || "");
  const hasVideo = Boolean(videoUrl);
  const videoBannerImage = images?.[0] || "/img/others/5.jpg";

  const floorPlan1 = localizedProperty?.floorPlanImageUrl1
    ? resolvePropertyMediaUrl(localizedProperty.floorPlanImageUrl1)
    : "";
  const floorPlan2 = localizedProperty?.floorPlanImageUrl2
    ? resolvePropertyMediaUrl(localizedProperty.floorPlanImageUrl2)
    : "";
  const hasFloorPlans = Boolean(floorPlan1 || floorPlan2);
  const ui = useTranslatedCopy(
    {
      loading: "Loading property...",
      featured: "Featured",
      forRent: "For Rent",
      forSale: "For Sale",
      sold: "Sold",
      rented: "Rented",
      description: "Description",
      propertyDetail: "Property Detail",
      homeArea: "Home Space",
      rooms: "Rooms",
      baths: "Baths",
      yearBuilt: "Year built",
      lotArea: "Lot Area",
      beds: "Bed Rooms",
      price: "Price",
      status: "Property Status",
      gallery: "From Our Gallery",
      amenities: "Amenities",
      location: "Location",
      floorPlans: "Floor Plans",
      plan1: "Plan 1",
      plan2: "Plan 2",
      locationTitle: "Property location",
      noDescription: "No description yet.",
      inquiryTitle: "Drop Message For Booking",
      inquiryName: "Your Name*",
      inquiryEmail: "Your e-Mail*",
      inquiryPhone: "Your Mobile Number*",
      inquiryMessage: "Write Message...",
      inquirySending: "Sending...",
      inquirySend: "Send Message",
      searchObjects: "Search Objects",
      searchPlaceholder: "Search your keyword...",
      followUs: "Follow us",
      propertyVideo: "Property Video",
      relatedProperties: "Related Properties",
      contactAgentHelp: "Contact us for site visit, pricing, and booking details.",
      forNegotiation: "Click for negotiation",
      bedrooms: "Bedrooms",
      bathrooms: "Bathrooms",
      squareFt: "sq meter",
      propertyWord: "Property",
      floorPlanDetails: "Floor plan details.",
    },
    locale,
    "property-detail-ui"
  );
  const descriptionText = pickLocalizedValue(localizedProperty, "description", locale, "");
  const hasDescription = hasDisplayValue(descriptionText);
  const lotAreaValue = pickLocalizedValue(localizedProperty, "area", locale, localizedProperty?.area);
  const propertyDetailItems = useMemo(() => {
    if (!localizedProperty) return [];
    const items = [];

    if (hasDisplayValue(localizedProperty.areaSqft)) {
      items.push({
        key: "homeArea",
        icon: "flaticon-square-shape-design-interface-tool-symbol",
        label: ui.homeArea,
        value: `${localizedProperty.areaSqft} ${ui.squareFt}`,
      });
    }
    if (hasDisplayValue(localizedProperty.rooms)) {
      items.push({
        key: "rooms",
        icon: "flaticon-bed-1",
        label: ui.rooms,
        value: localizedProperty.rooms,
      });
    }
    if (hasDisplayValue(localizedProperty.bathrooms)) {
      items.push({
        key: "baths",
        icon: "flaticon-shower-1",
        label: ui.baths,
        value: localizedProperty.bathrooms,
      });
    }
    if (hasDisplayValue(localizedProperty.yearBuilt)) {
      items.push({
        key: "yearBuilt",
        icon: "flaticon-calendar",
        label: ui.yearBuilt,
        value: localizedProperty.yearBuilt,
      });
    }
    if (hasDisplayValue(lotAreaValue)) {
      items.push({
        key: "lotArea",
        icon: "flaticon-expand",
        label: ui.lotArea,
        value: lotAreaValue,
      });
    }

    const statusLabel = getPropertyStatusLabel(localizedProperty, ui);
    if (hasDisplayValue(statusLabel)) {
      items.push({
        key: "status",
        icon: "flaticon-home-2",
        label: ui.status,
        value: statusLabel,
      });
    }

    return items;
  }, [localizedProperty, lotAreaValue, ui]);
  const propertyPageTitle = localizedProperty
    ? pickLocalizedValue(localizedProperty, "title", locale, ui.propertyWord)
    : t("pages.propertyDetail.title");

  if (loading) {
    return (
      <div>
        <Seo
          title={pickLocalizedValue(localizedProperty, "title", locale, "") ? `${pickLocalizedValue(localizedProperty, "title", locale, "")} | QTECH` : "Property Detail | QTECH"}
          titleAr={pickLocalizedValue(localizedProperty, "title", "ar", "") ? `${pickLocalizedValue(localizedProperty, "title", "ar", "")} | شركة تقنيات الجودة` : "تفاصيل العقار | شركة تقنيات الجودة"}
          description={pickLocalizedValue(localizedProperty, "shortDescription", locale, "") || pickLocalizedValue(localizedProperty, "description", locale, "") || "Property details from QTECH in Saudi Arabia."}
          descriptionAr={pickLocalizedValue(localizedProperty, "shortDescription", "ar", "") || pickLocalizedValue(localizedProperty, "description", "ar", "") || "تفاصيل عقار من شركة تقنيات الجودة في السعودية والخبر."}
          keywords={mergeSeoKeywords(
            "property Saudi Arabia, property for sale Saudi Arabia, property for rent Saudi Arabia, QTECH property",
            "investment property Saudi Arabia, real estate investment opportunity, buy property for investment Saudi Arabia, investment apartment Al Khobar"
          )}
          keywordsAr={mergeSeoKeywords(
            "عقار في الخبر، عقارات للبيع في الخبر، عقارات للإيجار في الخبر، شقق للبيع في الخبر، شقق للإيجار في الخبر، فلل للبيع في الخبر، شركة تقنيات الجودة",
            "عقار استثماري, فرصة استثمار عقاري, شراء عقار للاستثمار, شقة استثمارية في الخبر"
          )}
          path={`/property/${id}`}
          image={images[0] || fallbackPropertyImage}
        />
        <div className="qtech-loading-screen">
          <Loader label={ui.loading} className="qtech-loader--page" />
        </div>
      </div>
    );
  }

  const handleInquiryChange = (event) => {
    const { name, value } = event.target;
    setInquiryForm((current) => ({ ...current, [name]: value }));
  };

  const handleInquirySubmit = async (event) => {
    event.preventDefault();

    if (!localizedProperty?.id) return;

    try {
      setInquirySubmitting(true);
      setInquiryFeedback({ type: "", message: "" });

      const { json } = await fetchApiJson(`/api/properties/${localizedProperty.id}/inquiries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(inquiryForm),
      });

      if (!json?.success) {
        throw new Error(json?.message || "Failed to submit inquiry");
      }

      setInquiryForm({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setInquiryFeedback({
        type: "success",
        message: locale === "ar" ? "تم إرسال استفسارك بنجاح." : "Your inquiry has been sent successfully.",
      });
    } catch (submitError) {
      setInquiryFeedback({
        type: "error",
        message:
          submitError?.message ||
          (locale === "ar"
            ? "تعذر إرسال الاستفسار الآن. يرجى المحاولة مرة أخرى."
            : "We could not send your inquiry right now. Please try again."),
      });
    } finally {
      setInquirySubmitting(false);
    }
  };

  return (
    <div>
      <Seo
        title={pickLocalizedValue(localizedProperty, "title", locale, "") ? `${pickLocalizedValue(localizedProperty, "title", locale, "")} | QTECH` : "Property Detail | QTECH"}
        titleAr={pickLocalizedValue(localizedProperty, "title", "ar", "") ? `${pickLocalizedValue(localizedProperty, "title", "ar", "")} | شركة تقنيات الجودة` : "تفاصيل العقار | شركة تقنيات الجودة"}
        description={pickLocalizedValue(localizedProperty, "shortDescription", locale, "") || pickLocalizedValue(localizedProperty, "description", locale, "") || "Property details from QTECH in Saudi Arabia."}
        descriptionAr={pickLocalizedValue(localizedProperty, "shortDescription", "ar", "") || pickLocalizedValue(localizedProperty, "description", "ar", "") || "تفاصيل عقار من شركة تقنيات الجودة في السعودية والخبر."}
        keywords={mergeSeoKeywords(
          "property Saudi Arabia, property for sale Saudi Arabia, property for rent Saudi Arabia, QTECH property",
          "investment property Saudi Arabia, real estate investment opportunity, buy property for investment Saudi Arabia, investment apartment Al Khobar"
        )}
        keywordsAr={mergeSeoKeywords(
          "عقار في الخبر، عقارات للبيع في الخبر، عقارات للإيجار في الخبر، شقق للبيع في الخبر، شقق للإيجار في الخبر، فلل للبيع في الخبر، شركة تقنيات الجودة",
          "عقار استثماري, فرصة استثمار عقاري, شراء عقار للاستثمار, شقة استثمارية في الخبر"
        )}
        path={`/property/${id}`}
        image={images[0] || fallbackPropertyImage}
        structuredData={localizedProperty ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: pickLocalizedValue(localizedProperty, "title", locale, "Property"),
          description: pickLocalizedValue(localizedProperty, "shortDescription", locale, "") || pickLocalizedValue(localizedProperty, "description", locale, ""),
          image: images,
          address: localizedProperty.address || localizedProperty.city,
          datePosted: localizedProperty.publishedAt || localizedProperty.createdAt,
          offers: {
            "@type": "Offer",
            priceCurrency: localizedProperty.currency || "SAR",
            price: localizedProperty.price ? String(localizedProperty.price).replace(/[^0-9.]/g, "") : "0",
            availability: "https://schema.org/InStock"
          }
        } : undefined}
      />

      
      
      
      <div className="container mt-40 mb-40">
        <div className="massive-hero-banner" onClick={() => setLightboxIndex(activeImageIndex)}>
          <img src={images[activeImageIndex] || images[0] || fallbackPropertyImage} alt={propertyPageTitle} />
          <button type="button" className="show-all-photos-btn" onClick={(e) => { e.stopPropagation(); setLightboxIndex(0); }}>
            <i className="fas fa-th"></i> {locale === 'ar' ? 'عرض كل الصور' : 'Show all photos'}
          </button>
        </div>
        {images.length > 1 && (
          <div className="thumbnail-strip mt-20">
            {images.map((img, idx) => (
              <div 
                key={idx} 
                className={`thumbnail-item ${activeImageIndex === idx ? 'active' : ''}`} 
                onClick={() => { setActiveImageIndex(idx); }}
              >
                <img src={img} alt={`${propertyPageTitle} ${idx + 1}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="premium-property-detail-area pb-10">
      <div className="container">
        <div className="row qtech-property-details-layout">
          <div className="col-lg-8 col-md-12">
            {!loading && error && <div className="alert alert-danger">{error}</div>}

            {!loading && !error && localizedProperty && (
              <>
                <div className="premium-property-header mb-40">
                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                    <div>
                      <h1 className="premium-title">{propertyPageTitle}</h1>
                      <p className="premium-location">
                        <i className="flaticon-pin" /> {[pickLocalizedValue(localizedProperty, 'city', locale, localizedProperty?.city), pickLocalizedValue(localizedProperty, 'area', locale, localizedProperty?.area)].filter(Boolean).join(', ') || localizedProperty.location || '—'}
                      </p>
                    </div>
                    <div className="premium-price-badge">
                      {(() => {
                        const priceInfo = formatPropertyPrice(localizedProperty, { negotiationText: ui.forNegotiation });
                        return priceInfo.isNegotiation ? (
                          <a href={priceInfo.href} target="_blank" rel="noreferrer">{priceInfo.text}</a>
                        ) : (
                          <span>
                            {priceInfo.text || '—'}
                            {priceInfo.text ? <><span style={{margin:'0 5px'}}></span><small style={{fontSize:'14px'}}>{getPropertyPriceSuffix(localizedProperty, undefined, priceInfo.suffix)}</small></> : null}
                          </span>
                        );
                      })()}
                    </div>
                  </div>


                </div>

                {hasDescription ? (
                  <div className="premium-section">
                    <h3 className="premium-section-title">{ui.description}</h3>
                    <div className="premium-description-card prominent-description">
                      <div className="premium-description-text">
                        {renderFormattedText(descriptionText)}
                      </div>
                    </div>
                  </div>
                ) : null}

                {propertyDetailItems.length > 0 ? (
                  <div className="premium-section mt-50">
                    <h3 className="premium-section-title">{ui.propertyDetail}</h3>
                    <div className="prominent-details-grid">
                      {propertyDetailItems.map((item) => (
                        <div className="prominent-detail-card" key={item.key}>
                          <i className={`${item.icon} prominent-icon`}></i>
                          <div className="prominent-info">
                            <span className="prominent-label">{item.label}</span>
                            <strong className="prominent-value">{item.value}</strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {amenities.length > 0 ? (
                  <div className="premium-section mt-50">
                    <h3 className="premium-section-title">{locale === 'ar' ? 'المميزات' : 'Features'}</h3>
                    <div className="prominent-amenities-list">
                      {amenities.map((name) => (
                        <div className="prominent-amenity-item" key={name}>
                          <i className="fas fa-check-circle prominent-check"></i>
                          <span className="prominent-amenity-name">{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {hasMap ? (
                  <div className="premium-section">
                    <h3 className="premium-section-title">{ui.location}</h3>
                    <div className="property-details-google-map" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                      {mapEmbedUrl.includes('/embed') || mapEmbedUrl.includes('output=embed') ? (
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          <iframe
                            src={mapEmbedUrl}
                            width="100%"
                            height="100%"
                            frameBorder={0}
                            allowFullScreen=""
                            aria-hidden="false"
                            tabIndex={0}
                            title={ui.locationTitle}
                          />
                          <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10 }}>
                            <a
                              href={mapEmbedUrl.replace('&output=embed', '').replace('output=embed', '')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="theme-btn-1 btn btn-effect-1"
                              style={{ padding: '10px 20px', fontSize: '14px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)' }}
                            >
                              <i className="fas fa-directions" style={{ marginRight: '8px' }}></i> 
                              {locale === 'ar' ? 'احصل على الاتجاهات' : 'Get Directions'}
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            minHeight: '400px',
                            backgroundColor: '#f9f9f9',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #e4e4e4',
                            padding: '20px',
                            textAlign: 'center',
                          }}
                        >
                          <i className="flaticon-pin" style={{ fontSize: '50px', color: 'var(--ltn__secondary-color)', marginBottom: '15px' }} />
                          <h5 className="mb-20">{ui.locationTitle}</h5>
                          <a href={mapEmbedUrl} target="_blank" rel="noopener noreferrer" className="theme-btn-1 btn btn-effect-1">
                            View on Google Maps
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {hasFloorPlans ? (
                  <div className="premium-section">
                    <h3 className="premium-section-title">{ui.floorPlans}</h3>
                    <div className="ltn__apartments-plan-area product-details-apartments-plan">
                      <div className="ltn__tab-menu ltn__tab-menu-3 ltn__tab-menu-top-right-- text-uppercase--- text-center---">
                        <div className="nav">
                          {floorPlan1 ? <a className="active show" data-bs-toggle="tab" href="#liton_tab_3_1">{ui.plan1}</a> : null}
                          {floorPlan2 ? <a className={floorPlan1 ? '' : 'active show'} data-bs-toggle="tab" href="#liton_tab_3_2">{ui.plan2}</a> : null}
                        </div>
                      </div>
                      <div className="tab-content">
                        {floorPlan1 ? (
                          <div className="tab-pane fade active show" id="liton_tab_3_1">
                            <div className="ltn__apartments-tab-content-inner">
                              <div className="row">
                                <div className="col-lg-7">
                                  <div className="apartments-plan-img">
                                    <img className="qtech-property-detail-floorplan-image" src={floorPlan1} alt="Floor plan 1" />
                                  </div>
                                </div>
                                <div className="col-lg-5">
                                  <div className="apartments-plan-info ltn__secondary-bg--- text-color-white---">
                                    <h2>{ui.plan1}</h2>
                                    <p>{pickLocalizedValue(localizedProperty, 'shortDescription', locale, ui.floorPlanDetails)}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                        {floorPlan2 ? (
                          <div className={`tab-pane fade ${floorPlan1 ? '' : 'active show'}`} id="liton_tab_3_2">
                            <div className="ltn__apartments-tab-content-inner">
                              <div className="row">
                                <div className="col-lg-7">
                                  <div className="apartments-plan-img">
                                    <img className="qtech-property-detail-floorplan-image" src={floorPlan2} alt="Floor plan 2" />
                                  </div>
                                </div>
                                <div className="col-lg-5">
                                  <div className="apartments-plan-info ltn__secondary-bg--- text-color-white---">
                                    <h2>{ui.plan2}</h2>
                                    <p>{pickLocalizedValue(localizedProperty, 'shortDescription', locale, ui.floorPlanDetails)}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {hasVideo ? (
                  <div className="premium-section">
                    <h3 className="premium-section-title">{ui.propertyVideo}</h3>
                    <div
                      className="ltn__video-bg-img ltn__video-popup-height-500 bg-overlay-black-50 bg-image"
                      data-bs-bg={videoBannerImage}
                      style={{ backgroundImage: `url("${videoBannerImage}")`, borderRadius: '16px' }}
                    >
                      <a
                        className="ltn__video-icon-2 ltn__video-icon-2-border---"
                        href={videoUrl}
                        {...(isSocialVideoUrl(videoUrl) ? { target: '_blank', rel: 'noopener noreferrer' } : { 'data-rel': 'lightcase:myCollection' })}
                      >
                        <i className="fa fa-play" />
                      </a>
                    </div>
                  </div>
                ) : null}

              </>
            )}
          </div>

          <div className="col-lg-4">
            <aside className="premium-sidebar-sticky">
              <div className="premium-inquiry-card">
                <h4 className="inquiry-title">{ui.inquiryTitle}</h4>
                <p className="inquiry-subtitle">{ui.contactAgentHelp}</p>
                <form onSubmit={handleInquirySubmit}>
                  <input type="text" name="name" placeholder={ui.inquiryName} value={inquiryForm.name} onChange={handleInquiryChange} required />
                  <input type="email" name="email" placeholder={ui.inquiryEmail} value={inquiryForm.email} onChange={handleInquiryChange} required />
                  <input type="text" name="phone" placeholder={ui.inquiryPhone} value={inquiryForm.phone} onChange={handleInquiryChange} required />
                  <textarea name="message" placeholder={ui.inquiryMessage} value={inquiryForm.message} onChange={handleInquiryChange} required style={{ height: '120px' }} />
                  <button type="submit" className="btn theme-btn-1" disabled={inquirySubmitting}>
                    {inquirySubmitting ? ui.inquirySending : ui.inquirySend}
                  </button>
                  {inquiryFeedback.message ? (
                      <p
                        className={`mt-3 mb-0 ${inquiryFeedback.type === "error" ? "text-danger" : "text-success"}`}
                      >
                        {inquiryFeedback.message}
                      </p>
                    ) : null}
                  </form>
                </div>

                <div className="widget ltn__social-media-widget">
                  <h4 className="ltn__widget-title ltn__widget-title-border-2">{ui.followUs}</h4>
                  <div className="ltn__social-media-2">
                    <ul>
                      <li>
                        <a href="#" title="Facebook">
                          <i className="fab fa-facebook-f" />
                        </a>
                      </li>
                      <li>
                        <a href="#" title="Twitter">
                          <i className="fab fa-twitter" />
                        </a>
                      </li>
                      <li>
                        <a href="#" title="Linkedin">
                          <i className="fab fa-linkedin" />
                        </a>
                      </li>
                      <li>
                        <a href="#" title="Instagram">
                          <i className="fab fa-instagram" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
      {isLightboxOpen ? (
        <div className="qtech-property-lightbox" role="dialog" aria-modal="true" onClick={() => setLightboxIndex(-1)}>
          <button
            type="button"
            className="qtech-property-lightbox-close"
            onClick={() => setLightboxIndex(-1)}
            aria-label="Close image"
          >
            ×
          </button>
          {images.length > 1 ? (
            <button
              type="button"
              className="qtech-property-lightbox-nav qtech-property-lightbox-nav-prev"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex((current) => (current <= 0 ? images.length - 1 : current - 1));
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
          ) : null}
          <div className="qtech-property-lightbox-stage" onClick={(event) => event.stopPropagation()}>
            <img
              className="qtech-property-lightbox-image"
              src={images[lightboxIndex]}
              alt={`${pickLocalizedValue(localizedProperty, "title", locale, "Property")} ${lightboxIndex + 1}`}
            />
            <div className="qtech-property-lightbox-caption">
              {images.length > 1 ? `${lightboxIndex + 1} / ${images.length}` : null}
            </div>
          </div>
          {images.length > 1 ? (
            <button
              type="button"
              className="qtech-property-lightbox-nav qtech-property-lightbox-nav-next"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex((current) => (current >= images.length - 1 ? 0 : current + 1));
              }}
              aria-label="Next image"
            >
              ›
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
